<script setup lang="ts">
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { computed, nextTick, onMounted, reactive, watch } from 'vue';
import {
    type VariableConfig,
    type VariableValueType,
    VariableValueTypes,
} from '@/types/edit/VariableConfig.ts';
import { Input } from '@/components/ui/input';
import {
    TagsInput,
    TagsInputInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
} from '@/components/ui/tags-input';
import { Textarea } from '@/components/ui/textarea';
import {
    Check,
    ChevronsUpDown,
    LockIcon,
    LockOpenIcon,
    PlusIcon,
    Trash2Icon,
} from 'lucide-vue-next';
import {
    PROGRAMMING_LANGUAGE_OPTIONS_VARIABLE_ID,
    PROGRAMMING_LANGUAGE_VARIABLE_ID,
} from '@/constants/DefaultVariableConfigs.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { showAlertDialogAsync } from '@/wrappers/ShowAlertDialogAsync.ts';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import HotkeyIndicatorInline from '@/components/hotkey/HotkeyIndicatorInline.vue';
import { Hotkey } from '@/types/Hotkey.ts';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { VariableService } from '@/services/VariableService.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { programmingLanguageOptions } from '@/types/ProgrammingLanguage.ts';
import { cn } from '@/utils/RandomUtils.ts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';

const settingsStore = useSettingsStore();

interface ComponentState {
    // Variables
    defaultVariableConfigs: Record<VariableConfig['id'], VariableConfig>;
    customVariableConfigs: Record<VariableConfig['id'], VariableConfig>;
    // Dynamic variables
    dynamicVariableInputIdCopy: VariableConfig['id'] | undefined;
    dynamicVariableOutputIdCopy: VariableConfig['id'] | undefined;
    // Other
    queuedDefaultVariableChanges: Record<VariableConfig['id'], VariableConfig['value']>;
    selectedVariableConfigId: VariableConfig['id'] | undefined;
    variableKeyChangeEnabled: boolean;
    isProgrammingLanguagePopoverOpen: boolean;
    changesMade: boolean;
}

const state: ComponentState = reactive({
    // Variables
    defaultVariableConfigs: {},
    customVariableConfigs: {},
    // Dynamic variables
    dynamicVariableInputIdCopy: undefined,
    dynamicVariableOutputIdCopy: undefined,
    // Other
    queuedDefaultVariableChanges: {},
    selectedVariableConfigId: '98d8feff-4acf-4f05-92e7-e07b04250dc1',
    variableKeyChangeEnabled: false,
    isProgrammingLanguagePopoverOpen: false,
    changesMade: false,
});

/* region Config mutation */
const createNewCustomVariable = () => {
    const { variableConfigs, newVariableConfigId } = VariableConfigService.createCustomVariable(
        state.customVariableConfigs,
    );
    state.customVariableConfigs = variableConfigs;
    state.selectedVariableConfigId = newVariableConfigId;
};

const updateCustomVariableKey = (id: VariableConfig['id'], key: VariableConfig['key']) => {
    const { variableConfigs } = VariableConfigService.updateCustomVariableKey(
        state.customVariableConfigs,
        id,
        key,
    );
    state.customVariableConfigs = variableConfigs;
};

const updateCustomVariableValueType = (
    id: VariableConfig['id'],
    valueType: VariableConfig['valueType'],
) => {
    const { variableConfigs } = VariableConfigService.updateCustomVariableValueType(
        state.customVariableConfigs,
        id,
        valueType,
    );
    state.customVariableConfigs = variableConfigs;
};

const updateVariableValue = (
    id: VariableConfig['id'],
    type: VariableConfig['type'],
    value: VariableConfig['value'],
) => {
    if (type === 'DEFAULT') {
        state.queuedDefaultVariableChanges[id] = value;
    } else if (type === 'CUSTOM') {
        const { variableConfigs } = VariableConfigService.updateCustomVariableValue(
            state.customVariableConfigs,
            id,
            value,
        );
        state.customVariableConfigs = variableConfigs;
    }
};

const deleteCustomVariable = (id: VariableConfig['id']) => {
    const { variableConfigs } = VariableConfigService.deleteCustomVariable(
        state.customVariableConfigs,
        id,
    );
    state.customVariableConfigs = variableConfigs;

    if (Object.values(variableConfigs).length > 0) {
        state.selectedVariableConfigId = Object.values(variableConfigs)[0].id;
    } else {
        state.selectedVariableConfigId = undefined;
    }
};
/* endregion Config mutation */

/* region Event handlers */
const cancelChanges = async () => {
    state.dynamicVariableInputIdCopy = settingsStore.dynamicVariableInputId;
    state.dynamicVariableOutputIdCopy = settingsStore.dynamicVariableOutputId;
    await nextTick();
    state.changesMade = false;
};

const save = async () => {
    const variableValidationErrors = VariableConfigService.validate(state.customVariableConfigs);
    if (variableValidationErrors.length > 0) {
        createTextToast(
            'Validation failed',
            `There are ${variableValidationErrors.length} validation errors. Please fix them before saving: \n\n - ${variableValidationErrors.join('\n - ')}`,
            'destructive',
            false,
        );
        return;
    }

    // Update default variables
    for (const [id, value] of Object.entries(state.queuedDefaultVariableChanges)) {
        VariableService.updateDefaultVariableValue(id, value);
    }

    // Update custom variables
    VariableConfigService.updateCustomVariableConfigs(state.customVariableConfigs);

    settingsStore.dynamicVariableInputId = state.dynamicVariableInputIdCopy;
    settingsStore.dynamicVariableOutputId = state.dynamicVariableOutputIdCopy;
    await nextTick();
    state.changesMade = false;
    eventEmitter.emit('setSettingsDialogVisibility', {
        visible: false,
    });
};

const resetAllVariables = async () => {
    const userConfirmed = await showAlertDialogAsync({
        title: `Reset all variables back to their defaults?`,
        actionText: `Reset`,
    });

    if (userConfirmed) {
        state.customVariableConfigs = {};
        state.dynamicVariableInputIdCopy = PROGRAMMING_LANGUAGE_OPTIONS_VARIABLE_ID;
        state.dynamicVariableOutputIdCopy = PROGRAMMING_LANGUAGE_VARIABLE_ID;
    }
};

const setSelectedVariableConfigId = (id: VariableConfig['id'] | undefined) => {
    state.selectedVariableConfigId = id;
};

const setVariableKeyChangeEnabled = (enabled: boolean) => {
    state.variableKeyChangeEnabled = enabled;
};
/* endregion Event handlers */

const getVariableValue = (id: VariableConfig['id']): VariableConfig['value'] | undefined => {
    const variableConfigs = {
        ...state.defaultVariableConfigs,
        ...state.customVariableConfigs,
    };

    const variableConfig = variableConfigs[id];
    if (!variableConfig) {
        return undefined;
    }

    if (variableConfig.type === 'DEFAULT') {
        if (state.queuedDefaultVariableChanges[variableConfig.id]) {
            return state.queuedDefaultVariableChanges[variableConfig.id];
        } else {
            return VariableService.getDefaultVariableValue(variableConfig.id);
        }
    }

    return variableConfig.value;
};

/* region Computed */
const variablesConfigs = computed((): Record<VariableConfig['id'], VariableConfig> => {
    return VariableConfigService.getVariableConfigs({}, undefined, ['DEFAULT', 'CUSTOM']);
});

const selectedVariableConfig = computed((): VariableConfig | undefined => {
    if (!state.selectedVariableConfigId) {
        return undefined;
    }

    const allVariableConfigs = {
        ...state.defaultVariableConfigs,
        ...state.customVariableConfigs,
    };

    return allVariableConfigs[state.selectedVariableConfigId];
});

/* region Lifecycle */
onMounted(async () => {
    state.defaultVariableConfigs = VariableConfigService.getVariableConfigs({}, undefined, [
        'DEFAULT',
    ]);
    state.customVariableConfigs = JSON.parse(
        JSON.stringify(VariableConfigService.getVariableConfigs({}, undefined, ['CUSTOM'])),
    );
    state.dynamicVariableInputIdCopy = settingsStore.dynamicVariableInputId;
    state.dynamicVariableOutputIdCopy = settingsStore.dynamicVariableOutputId;
    await nextTick();
    state.changesMade = false;
});

watch(
    () => [
        state.customVariableConfigs,
        state.dynamicVariableInputIdCopy,
        state.dynamicVariableOutputIdCopy,
        state.queuedDefaultVariableChanges,
    ],
    async () => {
        state.changesMade = true;
    },
    { deep: true },
);
/* region Lifecycle */
</script>

<template>
    <main class="flex-shrink flex-grow overflow-y-auto p-4 pt-1">
        <section class="mb-8">
            <h2 class="mb-4 border-b pb-2">Variables</h2>

            <div class="flex w-full flex-col gap-y-4">
                <Label for="variable-select"> Variable </Label>
                <div class="flex w-full items-center gap-1.5">
                    <Select
                        :model-value="state.selectedVariableConfigId"
                        @update:model-value="setSelectedVariableConfigId"
                    >
                        <SelectTrigger
                            class="focus:ring-0 focus-visible:ring-0"
                            id="variable-select"
                        >
                            <SelectValue placeholder="Select a variable" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Default</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.defaultVariableConfigs).filter(
                                            (v) => v.type === 'DEFAULT',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.defaultVariableConfigs,
                                        ).filter((v) => v.type === 'DEFAULT')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                            <SelectGroup>
                                <SelectLabel>Custom</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.customVariableConfigs).filter(
                                            (v) => v.type === 'CUSTOM',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.customVariableConfigs,
                                        ).filter((v) => v.type === 'CUSTOM')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <Button type="button" variant="secondary" @click="createNewCustomVariable">
                        <PlusIcon />
                        New variable
                    </Button>
                </div>

                <template v-if="selectedVariableConfig">
                    <template v-if="selectedVariableConfig.type === 'CUSTOM'">
                        <!-- Variable key -->
                        <Label for="variable-key">Variable key</Label>
                        <div class="flex w-full items-center gap-1.5">
                            <div class="flex w-full items-center">
                                <Input
                                    type="text"
                                    placeholder="Enter your variable name"
                                    id="variable-key"
                                    @keydown.space.prevent
                                    :model-value="selectedVariableConfig.key"
                                    :disabled="!state.variableKeyChangeEnabled"
                                    @update:model-value="
                                        updateCustomVariableKey(
                                            selectedVariableConfig.id,
                                            String($event),
                                        )
                                    "
                                    class="flex-grow border border-green-500"
                                    :class="{
                                        'rounded-e-none': selectedVariableConfig.type === 'CUSTOM',
                                    }"
                                />
                                <TooltipProvider
                                    :delay-duration="150"
                                    v-if="selectedVariableConfig.type === 'CUSTOM'"
                                >
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                class="size-10 rounded-s-none"
                                                @click="
                                                    setVariableKeyChangeEnabled(
                                                        !state.variableKeyChangeEnabled,
                                                    )
                                                "
                                            >
                                                <LockIcon v-if="state.variableKeyChangeEnabled" />
                                                <LockOpenIcon v-else />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <span v-if="state.variableKeyChangeEnabled"
                                                >Lock variable key input</span
                                            >
                                            <span v-else>Unlock variable key input</span>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Button
                                v-if="selectedVariableConfig.type === 'CUSTOM'"
                                type="button"
                                variant="destructive"
                                @click="deleteCustomVariable(selectedVariableConfig.id)"
                            >
                                <Trash2Icon />
                                Delete variable
                            </Button>
                        </div>

                        <!-- Variable type -->
                        <Label for="variable-key">Variable type</Label>
                        <Select
                            :model-value="selectedVariableConfig.valueType"
                            @update:model-value="
                                updateCustomVariableValueType(
                                    selectedVariableConfig.id,
                                    $event as VariableValueType,
                                )
                            "
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Variable type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem
                                    v-for="type in VariableValueTypes"
                                    :key="type"
                                    :value="type"
                                >
                                    {{ type }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </template>

                    <!-- Value string input -->
                    <template
                        v-if="
                            selectedVariableConfig.type === 'DEFAULT' ||
                            selectedVariableConfig.type === 'CUSTOM'
                        "
                    >
                        <Label for="variable-value"> Variable value </Label>

                        <template
                            v-if="selectedVariableConfig.id === PROGRAMMING_LANGUAGE_VARIABLE_ID"
                        >
                            <Popover v-model:open="state.isProgrammingLanguagePopoverOpen">
                                <PopoverTrigger as-child>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        :aria-expanded="state.isProgrammingLanguagePopoverOpen"
                                        class="w-full justify-between"
                                    >
                                        {{
                                            getVariableValue(selectedVariableConfig.id) ??
                                            'Select language...'
                                        }}

                                        <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent class="w-[400px] p-0" align="start">
                                    <Command
                                        @update:model-value="
                                            updateVariableValue(
                                                selectedVariableConfig.id,
                                                selectedVariableConfig.type,
                                                $event as string,
                                            )
                                        "
                                    >
                                        <CommandInput placeholder="Search language..." />
                                        <CommandEmpty>No languages found.</CommandEmpty>
                                        <CommandList>
                                            <CommandGroup>
                                                <CommandItem
                                                    v-for="language in Object.values(
                                                        programmingLanguageOptions,
                                                    ).filter((v) => isNaN(Number(v)))"
                                                    :key="language"
                                                    :value="language"
                                                    class="hover:cursor-pointer"
                                                    @select="
                                                        state.isProgrammingLanguagePopoverOpen = false
                                                    "
                                                >
                                                    <Check
                                                        :class="
                                                            cn(
                                                                'mr-2 h-4 w-4',
                                                                (
                                                                    getVariableValue(
                                                                        selectedVariableConfig.id,
                                                                    ) ?? ''
                                                                ).toString() === language
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0',
                                                            )
                                                        "
                                                    />
                                                    {{ language }}
                                                </CommandItem>
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </template>
                        <template v-else>
                            <Textarea
                                v-if="selectedVariableConfig.valueType === 'string'"
                                :model-value="getVariableValue(selectedVariableConfig.id) as string"
                                @update:model-value="
                                    updateVariableValue(
                                        selectedVariableConfig.id,
                                        selectedVariableConfig.type,
                                        $event as string,
                                    )
                                "
                                id="variable-value"
                                :maxlength="500"
                                class="h-30"
                            />
                        </template>

                        <!-- Value array input -->
                        <TagsInput
                            v-if="selectedVariableConfig.valueType === 'array'"
                            :model-value="getVariableValue(selectedVariableConfig.id) as string[]"
                            @update:model-value="
                                updateVariableValue(
                                    selectedVariableConfig.id,
                                    selectedVariableConfig.type,
                                    $event as string[],
                                )
                            "
                            id="variable-value"
                        >
                            <TagsInputItem
                                v-for="item in getVariableValue(
                                    selectedVariableConfig.id,
                                ) as string[]"
                                :key="item"
                                :value="item"
                            >
                                <TagsInputItemText />
                                <TagsInputItemDelete />
                            </TagsInputItem>

                            <TagsInputInput placeholder='Values (type and press "Enter" to add)' />
                        </TagsInput>
                    </template>
                    <template
                        v-if="
                            selectedVariableConfig.type === 'PROMPT_OUTPUT' ||
                            selectedVariableConfig.type === 'PROMPT_USER_INPUT'
                        "
                    >
                        <Label for="variable-description" class="mb-2">Variable description</Label>
                        <Textarea
                            :model-value="selectedVariableConfig.description"
                            id="variable-description "
                            disabled
                        />
                    </template>
                </template>
            </div>
        </section>

        <section>
            <h2 class="mb-4 border-b pb-2">Dynamic variables</h2>

            <p class="mb-4 text-sm">
                With dynamic variables you can easily change the value of a variable based on the
                value of another variable during an interview.
            </p>

            <p class="mb-4 text-sm">
                You can use the configured hotkey
                <HotkeyIndicatorInline
                    :hotkey="Hotkey.ActionX4"
                    class="h-[1.25rem] items-center justify-center overflow-hidden rounded border px-1 py-0.5 text-xs tracking-wide"
                />
                to dynamically toggle set the output variable from the input variable after saving.
            </p>

            <Label for="variable-key">Input variable (array)</Label>
            <Select v-model="state.dynamicVariableInputIdCopy">
                <SelectTrigger class="mb-4 mt-2">
                    <SelectValue placeholder="Select an input variable" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        v-for="variableConfig in Object.values(variablesConfigs).filter(
                            (c) => c.valueType === 'array',
                        )"
                        :key="variableConfig.id"
                        :value="variableConfig.id"
                    >
                        {{ variableConfig.key }}
                    </SelectItem>
                </SelectContent>
            </Select>

            <Label for="variable-key">Output variable (string)</Label>
            <Select v-model="state.dynamicVariableOutputIdCopy">
                <SelectTrigger class="mb-4 mt-2">
                    <SelectValue placeholder="Select an output variable" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        v-for="variableConfig in Object.values(variablesConfigs).filter(
                            (c) => c.valueType === 'string',
                        )"
                        :key="variableConfig.id"
                        :value="variableConfig.id"
                    >
                        {{ variableConfig.key }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </section>
    </main>

    <footer class="flex justify-end border-t p-4">
        <div class="flex w-full justify-between">
            <div>
                <Button
                    type="button"
                    variant="destructive"
                    class="me-2"
                    @click="cancelChanges"
                    :disabled="!state.changesMade"
                >
                    Cancel
                </Button>
                <Button type="button" variant="secondary" @click="resetAllVariables">
                    Reset all variables
                </Button>
            </div>
            <Button type="button" @click="save" variant="success" :disabled="!state.changesMade">
                Save and close
            </Button>
        </div>
    </footer>
</template>
